import React, { useState, useEffect } from "react";
import { Dropdown, Spinner, Alert } from "react-bootstrap";
import {
  addContent,
  getContents,
  updateContent,
  deleteContent,
  signInWithEmail,
} from "../../../FIREBASECONFIG";
import { emojis } from "../bootstrap/alertData";
import { getUserPermissions, getUserRole } from "../../../APIS";

const BookingUsers = () => {
  // 🔹 Role hierarchy
  const roles = [
    { role: "Admin", level: 1 },
    { role: "Hotel Admin", level: 2 },
    { role: "Reservation Admin", level: 3 },
    { role: "User", level: 4 },
  ];

  const collectionName = "users";
  const hotelCollection = "hotels";
  const Imagesfolder = "";

  const role = getUserRole();
  const { module, service } = getUserPermissions();
  const isSuperAdmin = module === "*" && service === "*";

  const currentRole = roles.find(
    (r) => r.role.toLowerCase() === (role || "").toLowerCase()
  );

  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hotels, setHotels] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [editId, setEditId] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const itemsPerPage = 15;

  // 🔹 Fetch Hotels
  const fetchHotels = async () => {
    try {
      const hotelsData = await getContents(hotelCollection);
      setHotels(hotelsData);
    } catch (error) {
      setErrorMsg("Failed to load hotels");
    }
  };

  // 🔹 Fetch Users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const users = await getContents(collectionName);
      setUsersData(users);
    } catch (error) {
      setErrorMsg("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchHotels();
  }, []);

  // 🔹 Handle form input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    // Capitalize role before saving
    if (name === "role") {
      const capitalized =
        value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
      setFormData((prev) => ({ ...prev, [name]: capitalized }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // 🔹 Add / Update User
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      if (isEditMode) {
        await updateContent(collectionName, Imagesfolder, editId, formData);
        setSuccessMsg("User updated successfully!");
      } else {
        await signInWithEmail(
          formData.email,
          formData.password,
          formData.name,
          formData.role,
          formData.City
        );
        setSuccessMsg("User added successfully!");
      }

      setShowForm(false);
      setIsEditMode(false);
      setFormData({});
      fetchUsers();
    } catch (error) {
      console.error(error);
      setErrorMsg("Operation failed");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Delete User
  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await deleteContent(collectionName, id);
      setSuccessMsg("User deleted successfully!");
      fetchUsers();
    } catch (error) {
      setErrorMsg("Failed to delete user");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Edit User
  const handleEdit = (user) => {
    setFormData(user);
    setEditId(user.userid || user.id);
    setIsEditMode(true);
    setShowForm(true);
  };

  // 🔹 Search + Pagination
  const excludedRoles = new Set(["admin", "hoteladmin", "reservationadmin"]);

  const filteredUsers = usersData.filter((user) => {
    const normalizedRole = (user.role || "").toLowerCase().replace(/\s+/g, "");
    if (excludedRoles.has(normalizedRole)) {
      return false;
    }

    const lowerSearch = searchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(lowerSearch) ||
      user.email?.toLowerCase().includes(lowerSearch)
    );
  });

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = filteredUsers.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  // 🔹 Determine available roles
  const availableRoles = isSuperAdmin
    ? roles
    : roles.filter((r) => r.level > (currentRole?.level || 0));

  return (
    <div className="col-12">
      {/* Alerts */}
      <div className="col-12 mt-3">
        {successMsg && (
          <Alert
            variant="success"
            dismissible
            onClose={() => setSuccessMsg(null)}
          >
            {emojis.success} {successMsg}
          </Alert>
        )}
        {errorMsg && (
          <Alert variant="danger" dismissible onClose={() => setErrorMsg(null)}>
            {emojis.warning} {errorMsg}
          </Alert>
        )}
      </div>

      <div className="card">
        {/* Header */}
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="card-title m-0">Users</h4>
          <div className="d-flex align-items-center gap-2">
            {!showForm && (
              <input
                type="text"
                className="form-control"
                placeholder="Search by name or email"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(0);
                }}
              />
            )}
            {/* {!showForm && (
              <button
                className="btn btn-secondary btn-md"
                style={{ whiteSpace: "nowrap" }}
                onClick={() => {
                  setShowForm(true);
                  setIsEditMode(false);
                  setFormData({});
                }}
              >
                Add User
              </button>
            )} */}
          </div>
        </div>

        <div className="card-body">
          {showForm ? (
            // 🔹 FORM
            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* Name */}
                <div className="col-xl-6">
                  <div className="mb-3 row">
                    <label className="col-lg-4 col-form-label">
                      Name<span className="text-danger">*</span>
                    </label>
                    <div className="col-lg-6">
                      <input
                        type="text"
                        name="name"
                        value={formData.name || ""}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="Enter name..."
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div className="col-xl-6">
                  <div className="mb-3 row">
                    <label className="col-lg-4 col-form-label">
                      Email<span className="text-danger">*</span>
                    </label>
                    <div className="col-lg-6">
                      <input
                        type="email"
                        name="email"
                        value={formData.email || ""}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="Enter email..."
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Password */}
                <div className="col-xl-6">
                  <div className="mb-3 row">
                    <label className="col-lg-4 col-form-label">
                      Password<span className="text-danger">*</span>
                    </label>
                    <div className="col-lg-6">
                      <input
                        type="password"
                        name="password"
                        value={formData.password || ""}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="Enter password..."
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Role */}
                <div className="col-xl-6">
                  <div className="mb-3 row">
                    <label className="col-lg-4 col-form-label">
                      Role<span className="text-danger">*</span>
                    </label>
                    <div className="col-lg-6">
                      <select
                        name="role"
                        value={formData.role || ""}
                        onChange={handleChange}
                        className="form-control"
                        required
                      >
                        <option value="">Select Role</option>
                        {availableRoles.map((r) => (
                          <option key={r.role} value={r.role}>
                            {r.role}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Hotel (only for Hotel Admin & Reservation Admin) */}
                {(formData.role === "Hotel Admin" ||
                  formData.role === "Reservation Admin") && (
                  <div className="col-xl-6">
                    <div className="mb-3 row">
                      <label className="col-lg-4 col-form-label">Hotel</label>
                      <div className="col-lg-6">
                        <select
                          name="hotelId"
                          value={formData.hotelId || ""}
                          onChange={handleChange}
                          className="form-control"
                          required
                        >
                          <option value="">Select Hotel</option>
                          {hotels.map((hotel) => (
                            <option key={hotel.id} value={hotel.id}>
                              {hotel.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Buttons */}
                <div className="mb-3 row">
                  <div className="col-lg-10 ms-auto">
                    <button type="submit" className="btn btn-secondary">
                      Save
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-primary ms-2"
                      onClick={() => {
                        setShowForm(false);
                        setIsEditMode(false);
                        setFormData({});
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </form>
          ) : (
            // 🔹 TABLE
            <>
              {loading ? (
                <div className="text-center">
                  <Spinner animation="border" />
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedUsers.length > 0 ? (
                        paginatedUsers.map((user, i) => (
                          <tr key={user._id}>
                            <td>{currentPage * itemsPerPage + i + 1}</td>
                            <td>{user.name}</td>
                            <td>{user.email}</td>
                            <td style={{ textTransform: "capitalize" }}>
                              {user.role || "User"}
                            </td>
                            <td>
                              <Dropdown>
                                <Dropdown.Toggle variant="link">
                                  &#x2022;&#x2022;&#x2022;
                                </Dropdown.Toggle>
                                <Dropdown.Menu align="end">
                                  <Dropdown.Item
                                    onClick={() => handleEdit(user)}
                                  >
                                    Edit User
                                  </Dropdown.Item>
                                  {isSuperAdmin && (
                                    <Dropdown.Item
                                      onClick={() =>
                                        handleDelete(user.userid || user.id)
                                      }
                                    >
                                      Delete User
                                    </Dropdown.Item>
                                  )}
                                </Dropdown.Menu>
                              </Dropdown>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center">
                            No users found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                  Showing{" "}
                  {filteredUsers.length === 0
                    ? 0
                    : currentPage * itemsPerPage + 1}{" "}
                  to{" "}
                  {Math.min(
                    (currentPage + 1) * itemsPerPage,
                    filteredUsers.length
                  )}{" "}
                  of {filteredUsers.length} entries
                </div>
                <div>
                  <button
                    className={`btn btn-sm btn-outline-primary me-2 ${
                      currentPage === 0 ? "disabled" : ""
                    }`}
                    onClick={() =>
                      currentPage > 0 && setCurrentPage(currentPage - 1)
                    }
                  >
                    Prev
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i}
                      className={`btn btn-sm border me-2 ${
                        currentPage === i ? "btn-primary text-white " : ""
                      }`}
                      onClick={() => setCurrentPage(i)}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    className={`btn btn-sm me-2 btn-outline-primary ${
                      currentPage === totalPages - 1 ? "disabled" : ""
                    }`}
                    onClick={() =>
                      currentPage + 1 < totalPages &&
                      setCurrentPage(currentPage + 1)
                    }
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingUsers;
